import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { nsSharedStyles } from '../ns-styles/index.js';

@customElement('ns-registrar-mismatch-warning')
export class NSRegistrarMismatchWarning extends LitElement {
  @property({ type: String })
  registrar = '';

  // Open while hovered or while focus is anywhere inside, the button or the support link, until Escape
  @state()
  private __hovered = false;

  @state()
  private __focused = false;

  @state()
  private __dismissed = false;

  private get __open() {
    return (this.__hovered || this.__focused) && !this.__dismissed;
  }

  // Focus moving between the button and the link stays inside, so the details stay open for the link
  private __onFocusIn(e: FocusEvent) {
    if (!this.renderRoot.contains(e.relatedTarget as Node | null)) this.__dismissed = false;
    this.__focused = true;
  }

  private __onFocusOut(e: FocusEvent) {
    if (!this.renderRoot.contains(e.relatedTarget as Node | null)) this.__focused = false;
  }

  static styles = [
    nsSharedStyles,
    css`
      :host {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      :host([hidden]) {
        display: none;
      }

      .trigger {
        display: flex;
      }

      button {
        display: inline-flex;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        color: var(--ns-theme-red);
      }

      button:focus-visible {
        outline: 2px solid var(--primary-color);
        outline-offset: 2px;
      }

      .details {
        position: absolute;
        top: 20px;
        right: 0;
        width: 200px;
        padding: var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);
        background: var(--primary-warning-background-color);
        color: var(--ns-theme-black);
      }

      .details a {
        color: inherit;
      }

      .details[hidden] {
        display: none;
      }
    `,
  ];

  render() {
    return html`
      <div
        class="trigger"
        @mouseenter=${() => {
          this.__hovered = true;
          this.__dismissed = false;
        }}
        @mouseleave=${() => (this.__hovered = false)}
        @focusin=${this.__onFocusIn}
        @focusout=${this.__onFocusOut}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Escape') this.__dismissed = true;
        }}
      >
        <button type="button" aria-expanded=${this.__open ? 'true' : 'false'} aria-controls="details">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="20px"
            viewBox="0 0 24 24"
            width="20px"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M0 0h24v24H0z" fill="none" />
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <span class="visually-hidden">${this.registrar} details mismatch, activate for details</span>
        </button>
        <div class="details" id="details" ?hidden=${!this.__open}>
          Your organisation's details on ${this.registrar} does not match your organisation's details on other
          registrar. Please contact our support team at
          <a class="clickable" href="mailto:support@nodestream.com">support@nodestream.com</a>
          for help resolving this.
        </div>
      </div>
    `;
  }
}
